import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import DBTable from '../../handlers/DatabaseHandler';
import {
    getCurrentLanguage,
    setLanguagePreference,
    getLanguageInfo,
    getSupportedLanguages,
    resolveLanguagePreference
} from '../../utils/languageUtils';
import { COLLECTION } from '../../config/mongoCollections.js';

function optionButtonClass(selected, disabled = false) {
    return `flex items-center gap-3 rounded-full border px-4 py-3 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        selected
            ? 'border-accent bg-accent text-accent-foreground shadow-sm'
            : 'border-edge bg-surface text-ink-muted hover:border-edge-strong hover:bg-surface-muted'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;
}

const LanguageSwitcher = ({
    compact = false,
    responsiveCompact = false,
    darkSurface = false,
    onOpenChange,
    /** `list` = settings-style radio buttons (no dropdown). Default keeps nav dropdown. */
    variant = 'dropdown'
}) => {
    const { t, i18n } = useTranslation();
    const { currentUser, isAuthenticated, updateLanguagePreference } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => getCurrentLanguage());
    const [isBufferActive, setIsBufferActive] = useState(false);
    const [bufferTimeout, setBufferTimeout] = useState(null);

    const languages = getSupportedLanguages();

    // Initialize database handler for user profile updates
    const userTable = new DBTable(
        COLLECTION.users,
        "User_ID",
        {
            User_ID: "",
            First_Name: "",
            Last_Name: "",
            Nickname: "",
            Email_Address: "",
            Tel: "",
            User_Role: "",
            School_Name: "",
            Form: "",
            direct_marketing: false,
            email_list: false,
            card_id: "",
            ability_6d: [1.5, 1.5, 1.5, 1.5, 1.5, 1.5],
            eddy_recommendation_title: "",
            eddy_recommendation_text: "",
            needs_recommendation_regeneration: true,
            trajectory_events: [],
            trajectory_connections: [],
            trajectory_analyses: [],
            event_types: ['academic', 'extracurricular', 'personal'],
            preferred_language: "en"
        }
    );

    // Selector must mirror resolved user intent (localStorage preferred over stale DB).
    useEffect(() => {
        if (i18n.languages && i18n.languages.length < 3) {
            i18n.languages = ['en', 'tc', 'sc'];
        }

        const resolved = resolveLanguagePreference({
            dbLanguage: isAuthenticated ? currentUser?.preferred_language : null,
            i18nLanguage: i18n.language
        });
        setCurrentLanguage(resolved);
    }, [i18n.language, i18n.languages, isAuthenticated, currentUser?.preferred_language]);

    // Cleanup effect to clear timeout on unmount
    useEffect(() => {
        return () => {
            if (bufferTimeout) {
                clearTimeout(bufferTimeout);
            }
        };
    }, [bufferTimeout]);

    const handleLanguageChange = async (languageCode) => {
        // Prevent language change if buffer is active
        if (isBufferActive) {
            // console.log('LanguageSwitcher: Buffer active, ignoring language change request');
            return;
        }

        // Don't change if it's the same language
        if (languageCode === currentLanguage) {
            setIsOpen(false);
            return;
        }

        // Close dropdown immediately when language is selected
        setIsOpen(false);

        try {
            // console.log('LanguageSwitcher: Changing language to:', languageCode);

            // Activate buffer immediately
            setIsBufferActive(true);

            // Clear any existing timeout
            if (bufferTimeout) {
                clearTimeout(bufferTimeout);
            }

            const success = await setLanguagePreference(
                languageCode,
                isAuthenticated ? userTable : null,
                isAuthenticated ? currentUser?.User_ID : null,
                updateLanguagePreference
            );

            if (success) {
                setCurrentLanguage(languageCode);

                // Set 5-second buffer timeout
                const timeout = setTimeout(() => {
                    setIsBufferActive(false);
                    setBufferTimeout(null);
                    // console.log('LanguageSwitcher: Buffer deactivated');
                }, 5000);

                setBufferTimeout(timeout);
                // console.log('LanguageSwitcher: Buffer activated for 5 seconds');
            } else {
                // If failed, deactivate buffer immediately
                setIsBufferActive(false);
            }
        } catch (error) {
            console.error('Error changing language:', error);
            // If error, deactivate buffer immediately
            setIsBufferActive(false);
        }
    };

    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.language-switcher')) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const getCurrentLanguageInfo = () => {
        return getLanguageInfo(currentLanguage);
    };

    if (variant === 'list') {
        return (
            <div
                className="grid gap-2 sm:grid-cols-3"
                role="radiogroup"
                aria-label={t('navigation.selectLanguage')}
            >
                {languages.map((language) => {
                    const selected = currentLanguage === language.code;
                    return (
                        <button
                            key={language.code}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            disabled={isBufferActive}
                            onClick={() => handleLanguageChange(language.code)}
                            className={optionButtonClass(selected, isBufferActive)}
                        >
                            <span className="text-lg leading-none" aria-hidden>
                                {language.flag}
                            </span>
                            <span className="min-w-0 truncate">{language.name}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={`relative language-switcher ${compact ? 'max-w-[100%]' : ''}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                disabled={isBufferActive}
                className={`inline-flex items-center gap-1 font-roboto font-medium leading-snug min-h-6 box-border whitespace-nowrap rounded-full focus:outline-none focus:ring-2 focus:ring-ring ${
                    isBufferActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                    compact
                        ? 'px-2 py-1 text-sm'
                        : responsiveCompact
                            ? 'px-1.5 py-1 text-sm xl:px-2.5 xl:text-base'
                            : 'px-2.5 py-1 text-sm'
                } ${isBufferActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
                    darkSurface
                        ? 'bg-white/10 border border-white/25 hover:bg-white/15 text-white'
                        : 'bg-surface border border-edge-strong hover:bg-surface-muted text-ink'
                }`}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={t('navigation.selectLanguage')}
            >
                <span className="text-sm leading-none xl:text-base" aria-hidden>
                    {getCurrentLanguageInfo().flag}
                </span>
                {!compact && (
                    <span className={`${responsiveCompact ? 'hidden xl:inline' : ''} ${darkSurface ? 'text-white/90' : 'text-ink-muted'}`}>
                        {getCurrentLanguageInfo().name}
                    </span>
                )}
                {isBufferActive && !compact && (
                    <span className={`text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full ${responsiveCompact ? 'hidden xl:inline' : ''}`}>
                        {t('common.syncing')}
                    </span>
                )}
                <svg
                    className={`block h-4 w-4 shrink-0 self-center transition-transform duration-200 ease-out motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''} ${darkSurface ? 'text-white/70' : 'text-ink-subtle'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            <div
                className={`absolute mt-2 w-48 overflow-hidden shadow-lg origin-top-right transition-all duration-200 ease-out motion-reduce:transition-none ${compact ? 'right-0' : 'right-0'} ${
                    darkSurface
                        ? 'z-[200] rounded-xl border border-white/15 bg-[#121212]/90 backdrop-blur-xl shadow-black/30'
                        : 'z-[110] rounded-md border border-edge bg-surface-elevated'
                } ${
                    isOpen
                        ? 'pointer-events-auto visible translate-y-0 scale-100 opacity-100'
                        : 'pointer-events-none invisible -translate-y-1 scale-[0.98] opacity-0'
                }`}
                role="listbox"
                aria-hidden={!isOpen}
            >
                    {isBufferActive && (
                        <div className={`px-4 py-2 border-b ${darkSurface ? 'bg-white/5 border-white/15' : 'bg-orange-50 border-orange-200'}`}>
                            <p className={`text-xs ${darkSurface ? 'text-[#A0C4FF]' : 'text-orange-700'}`}>
                                {t('common.syncingWithServer')}
                            </p>
                        </div>
                    )}
                    {languages.map((language) => (
                        <button
                            key={language.code}
                            onClick={() => !isBufferActive && handleLanguageChange(language.code)}
                            disabled={isBufferActive}
                            className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                                isBufferActive ? 'cursor-not-allowed' : 'cursor-pointer'
                            } ${
                                darkSurface
                                    ? currentLanguage === language.code
                                        ? 'bg-white/10 text-white'
                                        : 'text-white/85 hover:bg-white/5 hover:text-white'
                                    : currentLanguage === language.code
                                        ? 'bg-accent/30 text-ink'
                                        : 'text-ink-muted hover:bg-surface-muted'
                            } ${isBufferActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            <span className="text-lg">{language.flag}</span>
                            <span>{language.name}</span>
                            {currentLanguage === language.code && (
                                <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>
                    ))}
            </div>
        </div>
    );
};

export default LanguageSwitcher; 
